import React, { useMemo } from 'react';

import { CircleHelp } from 'lucide-react'
import { Area, AreaChart, CartesianGrid, XAxis, TooltipProps } from 'recharts';
import {
	Card,
	CardContent,
	CardHeader,
	CardTitle
} from '@/components/ui/card';
import {
	ChartConfig,
	ChartContainer,
	ChartTooltip,
	ChartTooltipContent
} from '@/components/ui/chart';
import { cn } from '@/lib/utils'
import { CardDescription } from '../ui/card';

const chartConfig = {
	desktop: {
		label: "Desktop",
		// color: "hsl(var(--chart-1))",
		color: '#C0E6FF'
	},
} satisfies ChartConfig

interface StatsAreaChartProps {
	title: string
	data: Array<any>;
	xDataKey: string;
	yDataKey: string;

	description?: string
	icon?: React.ReactNode
	className?: string
	descriptionStyleOverride?: string
}

interface CustomTooltipProps extends TooltipProps<number, string> {}

const CustomTooltip: React.FC<CustomTooltipProps> = ({
  active,
  payload,
  label,
}) => {
  if (!active || !payload || payload.length === 0) {
    return null;
  }

  // Assuming the `label` is a date string.
  const formattedDate = new Date(label as string)
    .toISOString()
    .substring(0, 10);
  // Use the first payload item (for 'ACTIVE_ACCOUNTS')
  const dataKey = payload[0].name;
  const value = payload[0].value as number;

  return (
    <div className="p-2 bg-[#011829]/80 backdrop-blur-md text-white rounded shadow">
      <p className="mb-1 text-sm font-medium">{formattedDate}</p>
      <p className="text-sm">
        {dataKey?.replace(/_/g, ' ')}: {value.toLocaleString()}
      </p>
    </div>
  );
};


export const StatsChart = (props: StatsAreaChartProps) => {
	const { title, data, icon, xDataKey, yDataKey, className, description, descriptionStyleOverride } = props;

	const filteredData = useMemo(() => {
		if (!data) return [];

		const _data = data.slice();
		_data.pop();

		return _data.reverse()
	}, [data])

	return (
		<>
			<Card className={cn('overflow-hidden bg-[#C0E6FF]/10 backdrop-blur-md border border-white/20 rounded-lg shadow-lg', className)}>
				<CardHeader className="flex flex-col space-y-0 p-0">
					<div className='flex flex-row items-center'>
						{icon && (
							<div className="rounded-full p-2 transition-colors">
								{icon}
							</div>
						)}
						<div className='flex-1'>
							<CardTitle className="text-md font-medium">{title}</CardTitle>
						</div>
					</div>
					<CardDescription className={cn('px-2', descriptionStyleOverride)}>
						{description}
					</CardDescription>
				</CardHeader>
				<CardContent className='p-4'>
					{ !!filteredData?.length && <ChartContainer config={chartConfig}>
						<AreaChart
							accessibilityLayer
							data={filteredData}
							margin={{
								top: 12,
								bottom: 0
							}}
						>
							<defs>
								<linearGradient id="gradient" x1="0" y1="0" x2="0" y2="1">
									<stop offset="5%" stopColor="#C0E6FF" stopOpacity={0.8} />
									<stop offset="95%" stopColor="#C0E6FF" stopOpacity={0} />
								</linearGradient>
							</defs>
							<CartesianGrid vertical={false} />
							<XAxis
								dataKey={xDataKey}
								tickLine={false}
								axisLine={false}
								tickMargin={8}
								ticks={[filteredData[0][xDataKey], filteredData[filteredData.length - 1][xDataKey]]}
								padding={{ left: 20, right: 20 }}
								// padding={{left: 40, right: 40}}
								tickFormatter={(value) =>
									new Date(value).toLocaleDateString('en-US', {
										timeZone: 'UTC',
										month: 'short',
										day: '2-digit'
									})
								}
							/>
							<ChartTooltip
								cursor={{ stroke: '#C0E6FF', strokeWidth: 2, strokeDasharray: '3 3' }}
								content={<CustomTooltip />}
							/>
							<Area
								dataKey={yDataKey}
								type="natural"
								fill="url(#gradient)"
								fillOpacity={0.4}
								stroke="var(--color-desktop)"
							/>
						</AreaChart>
					</ChartContainer>}

					{/* {description && (
						<TooltipProvider>
							<Tooltip>
								<TooltipTrigger asChild>
									<p className="mt-2 cursor-help truncate text-sm text-muted-foreground">
										{description}
									</p>
								</TooltipTrigger>
								<TooltipContent>
									<p className="max-w-xs break-all">{description}</p>
								</TooltipContent>
							</Tooltip>
						</TooltipProvider>
					)} */}
				</CardContent>
			</Card>
		</>
	)
}